// permissions.ts

export type AccessLevel = 'none' | 'view' | 'create' | 'full' | 'dummy' | 'own' | 'all' | 'onboarded';

export interface Permission {
  hiddenMenus?: string[];
  remediationPlan?: AccessLevel;
  deviceManagement?: AccessLevel;
  groupPolicies?: AccessLevel;
  tenantManagement?: AccessLevel;
  accessControl?: AccessLevel;
  support?: AccessLevel;
  dashboard?: AccessLevel;
  softwareDeployment?: AccessLevel;
  scriptCenter?: AccessLevel;
  report?: AccessLevel;
  // Activity log (AuditAPI). 'view' = the selected tenant only; 'all' = also the global and cross-tenant views.
  activityLog?: AccessLevel;
}

export type Role = 'tenantuser' | 'grouptenantadmin' | 'tenantadmin' | 'globaladmin' | 'reseller';

export type PermissionsConfig = Record<Role, Permission>;

// Exactly the roles Defatom.Cloud.AuditAPI's tenant endpoints allow ([Roles(Globaladmin, Reseller, Tenantadmin)]).
// Gates both the /activity-log route and its sidebar entry. UI gating only - the API enforces access (and the
// tenant-access rules) itself.
export const ACTIVITY_LOG_ALLOWED_ROLES: string[] = ['globaladmin', 'reseller', 'tenantadmin'];

// Levels that grant the ability to create/modify/act (as opposed to read-only "view").
export const EDIT_ACCESS_LEVELS: AccessLevel[] = ['create', 'full', 'own', 'all', 'onboarded'];

export const isEditAccess = (level?: AccessLevel): boolean => !!level && EDIT_ACCESS_LEVELS.includes(level);

export const PERMISSIONS: PermissionsConfig = {
  tenantuser: {
    hiddenMenus: ['TenantManagement', 'AccessControl', 'Support', 'PackageManager', 'Settings', 'Report', 'ActivityLog'],
    remediationPlan: 'view',
    deviceManagement: 'view',
    groupPolicies: 'view',
    softwareDeployment: 'view',
    scriptCenter: 'view',
    report: 'none',
    activityLog: 'none',
  },
  grouptenantadmin: {
    hiddenMenus: ['TenantManagement', 'AccessControl', 'Scope', 'Support', 'PackageManager', 'Settings', 'Report', 'ActivityLog'],
    remediationPlan: 'create',
    deviceManagement: 'view',
    groupPolicies: 'create',
    softwareDeployment: 'create',
    scriptCenter: 'create',
    report: 'none',
    activityLog: 'none',
  },
  tenantadmin: {
    hiddenMenus: ['TenantManagement', 'PackageManager', 'Settings'],
    remediationPlan: 'create',
    deviceManagement: 'full',
    groupPolicies: 'create',
    tenantManagement: 'own',
    accessControl: 'own',
    softwareDeployment: 'create',
    scriptCenter: 'create',
    report: 'create',
    activityLog: 'view',
  },
  globaladmin: {
    hiddenMenus: [],
    remediationPlan: 'create',
    deviceManagement: 'full',
    groupPolicies: 'create',
    tenantManagement: 'all',
    accessControl: 'all',
    support: 'all',
    dashboard: 'dummy',
    softwareDeployment: 'create',
    scriptCenter: 'create',
    report: 'create',
    activityLog: 'all',
  },
  reseller: {
    hiddenMenus: ['PackageManager', 'Settings'],
    remediationPlan: 'create',
    deviceManagement: 'full',
    groupPolicies: 'create',
    tenantManagement: 'onboarded',
    dashboard: 'dummy',
    softwareDeployment: 'create',
    scriptCenter: 'create',
    report: 'create',
    activityLog: 'view',
  },
};
